class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    // The empty state is also set on the <cart-drawer> element itself, and
    // it hides the header + item list — clear it too, otherwise the first
    // item added to an empty cart shows a blank drawer.
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ==========================================================================
   Cart progress bar fill animation (snippets/cart-drawer-progress.liquid)

   Dawn's own cart AJAX flow (CartDrawerItems.renderContents /
   CartItems.updateQuantity in cart.js) replaces #CartDrawer's innerHTML
   wholesale on every add/remove/quantity change, using fresh HTML rendered
   server-side from the real `cart` object (cart.item_count). That HTML
   already carries the correct target width for .cart-progress__fill — but
   because the element is destroyed and recreated on every swap, a plain
   CSS `transition: width` never has an old value in the browser's eyes to
   animate from; it just snaps straight to the new value.

   Fix: track the last-rendered fill % ourselves (module-level, survives
   across swaps since this script itself isn't reloaded) and manually FLIP
   the new element from that old value to its real target on every DOM
   mutation inside the drawer, so the bar reads as animating smoothly
   between cart states instead of jumping. This also covers the drawer
   opening/reopening and any section re-render, since all of those go
   through the same innerHTML swap + MutationObserver path. */
(function () {
  let lastFillPercent = null;

  function flipProp(el, cssProp, target) {
    if (!el) return;
    el.style.transition = 'none';
    el.style[cssProp] = `${lastFillPercent}%`;
    // Force a synchronous reflow so the reset above actually paints before
    // the transition is re-enabled below (classic FLIP technique).
    // eslint-disable-next-line no-unused-expressions
    el.offsetWidth;
    el.style.transition = '';
    requestAnimationFrame(() => {
      el.style[cssProp] = `${target}%`;
    });
  }

  function animateFill(fillEl) {
    const target = parseFloat(fillEl.dataset.fillPercent || '0');
    if (lastFillPercent === null || lastFillPercent === target) {
      lastFillPercent = target;
      return;
    }
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      lastFillPercent = target;
      return;
    }
    flipProp(fillEl, 'width', target);
    lastFillPercent = target;
  }

  function init() {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;

    const initialFill = drawer.querySelector('.cart-progress__fill');
    if (initialFill) lastFillPercent = parseFloat(initialFill.dataset.fillPercent || '0');

    new MutationObserver(() => {
      const fillEl = document.querySelector('.cart-progress__fill');
      if (fillEl) animateFill(fillEl);
    }).observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
